import { useMemo, useEffect, useState, useCallback } from 'react';
import type { ColumnDef } from '@tanstack/react-table';
import { useTranslation } from 'react-i18next';
import useCurrentLang from '@/hooks/useCurrentLang';

import { errorHandler } from '@/utils/toastHandler';
import PageContainer from '@/components/Container/PageContainer';

import DataTable, { TypeColumn, FileMenu } from '@/components/DataTable';
import { Flex, LoadingPage } from '@/layout';
import { successHandler } from '@/utils/toastHandler';
import Loading from '@/components/UI/Loading';
import { extensionValidator } from '@/utils/extensionValidator';
import { StorageBar } from '@/components/UI/StorageBar';
import { Button } from '@/components/UI/Button';
import { Delete, Documents, Link } from '@/components/Icon';
import { deleteUserMeetingRecordings, getUserMeetingRecordings } from '@/api/event.api';
import { Text } from '@/components/UI/Typography/Text';
import { theme } from '@/theme/Theme';
import { convertToFileSize } from '@/utils/convert';
import DeleteModal from './Modal';

type DataRow = MeetingVideoRecord

const MeetingVideo = () => {
  // @ts-ignore
  const { t } = useTranslation();
  const lang = useCurrentLang();

  const tabLabel = t('pages.event.meetingVideo.table.columns', { returnObjects: true });

  const [fileList, setFileList] = useState<MeetingVideoRecord[]>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showDocumentStorageAlmostFullModal, setShowDocumentStorageAlmostFullModal] = useState<boolean>(false);
  const [showDocumentStorageFullModal, setShowDocumentStorageFullModal] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [selectedId, setSelectedId] = useState<string>('');

  const columns: ColumnDef<DataRow>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: tabLabel[0],
        cell: ({ row }) => (
          <Flex alignItems="center" gridGap={'8px'}>
            <Documents.DocumentsSVG fill={theme.colors.primary500} />
            <Text>
              {row.original.name}
            </Text>
          </Flex>
        ),
      },
      {
        accessorKey: 'size',
        header: tabLabel[2],
        cell: ({ row }) => convertToFileSize(parseInt(row.original.size)),
      },
      {
        accessorKey: 'datetime',
        header: tabLabel[3],
      },
      {
        accessorKey: 'action',
        header: tabLabel[4],
        cell: ({ row }) => {
          if (row.original.name === '..') {
            return null;
          }
          const { url, recordID } = row.original;
          //   let added = hasMatchingProps(docLibrary, fileList, 'id', 'document_library_id');
          return (
            <Flex>
              <a href={url} target="_blank" rel="noopener noreferrer">
                <Button
                  size="sm"
                  type="button"
                >
                  <Link.LinkSVG />
                </Button>
              </a>
              <Button
                size="sm"
                type="button"
                onClick={() => {
                  setSelectedId(recordID);
                  setShowDeleteModal(true);
                }}
              >
                <Delete.DeleteSVG fill={theme.colors.primary500} />
              </Button>
            </Flex>
          );
        },
      },
    ],
    [t, tabLabel, lang]
  );

  const initData = useCallback(async () => {
    setIsLoading(true);
    try {
      const res: MeetingVideoResponse = (await getUserMeetingRecordings()).data;
      // 先將影片陣列順序反轉，讓最新的影片顯示在最前面
      res.data.video.reverse();
      setFileList(res.data.video);
    } catch (error) {
      errorHandler(error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    initData();
  }, [initData]);

  return (
    <PageContainer title={t('pages.event.meetingVideo.title')}>
      <LoadingPage isLoading={isLoading} />
      <DeleteModal initData={initData} recordID={selectedId || ''} showModal={showDeleteModal} setShowModal={setShowDeleteModal} setIsLoading={setIsLoading} />

      <div style={{ width: '100%', maxWidth: '1200px', background: '#ffffff', padding: '30px 40px', borderRadius: 15 }}>
        <StorageBar
          showStorageAlmostFullModal={showDocumentStorageAlmostFullModal}
          showStorageFullModal={showDocumentStorageFullModal}
          setShowStorageAlmostFullModal={setShowDocumentStorageAlmostFullModal}
          setShowStorageFullModal={setShowDocumentStorageFullModal}
        />
        {fileList ? <DataTable columns={columns} data={fileList} canSearch /> : <Loading />}
      </div>
    </PageContainer>
  );
};

export default MeetingVideo;
